import * as React from 'react';
import type { StoryMeta } from '@features/devtools/registry/types';
import type { NodeTemplate } from '@features/flows/palette/registry';
import NodeInspectorPanel from './NodeInspectorPanel';
import { useOutputInspector } from '@/context/output-inspector';
import { useRuntimeStore } from '@features/flows/runtime/useRuntimeStore';

type LogEntry = string | { level?: 'debug'|'info'|'warn'|'error'; msg: string };

type Props = {
  nodeId?: string;
  inputs?: unknown;
  outputs?: unknown;
  logs?: LogEntry[];
  iterCount?: number; // how many iterations to simulate
};

function NodeInspectorPreview({ nodeId = 'n1', inputs, outputs, logs = [], iterCount = 3 }: Props) {
  const { openFor, setOpen } = useOutputInspector();
  const resetRun = useRuntimeStore((s) => s.resetRun);
  const apply = useRuntimeStore((s) => s.applyTelemetry);

  React.useEffect(() => {
    // Start with a clean ephemeral run envelope for preview
    resetRun(undefined, undefined);

    const now = Date.now();
    const N = Math.max(1, iterCount);
    for (let i = 0; i < N; i++) {
      const t0 = now + i * 100;
      // Simulate a start event (captures inputs when provided)
      apply({ action: 'start', nodeId, ts: t0, scope: typeof inputs !== 'undefined' ? { inputs: { ...(inputs as any), i } } : undefined, iteration: i } as any);
      // Append any provided log entries as telemetry markers
      const storyLogs = Array.isArray(logs) && logs.length ? logs : [{ level: 'info', msg: `hello iter ${i}` }];
      storyLogs.forEach((l, k) => {
        const entry = typeof l === 'string' ? { level: 'info' as const, msg: l } : (l || { level: 'info', msg: '' });
        apply({ action: 'marker', nodeId, ts: t0 + 20 + k, payload: { level: entry.level ?? 'info', line: `${entry.msg} [i=${i}]` }, iteration: i } as any);
      });
      // Simulate a completion event (captures outputs when provided)
      apply({ action: 'complete', nodeId, ts: t0 + 50, scope: typeof outputs !== 'undefined' ? { outputs: { ...(outputs as any), i } } : undefined, iteration: i } as any);
    }

    // Open inspector for the pinned node
    openFor(nodeId);
    return () => setOpen(false);
  }, [nodeId, inputs, outputs, JSON.stringify(logs), openFor, setOpen, apply, resetRun]);

  const pinsByNodeId = React.useMemo(() => ({
    [nodeId]: {
      exec: { in: [{ id: 'in' }], out: [{ id: 'next' }] },
      data: { in: [{ id: 'value', type: 'number', required: true }], out: [{ id: 'value', type: 'number' }] },
    },
  }), [nodeId]);

  const tpl: NodeTemplate = {
    key: 'fs.list',
    title: 'Fs List',
    category: 'fs',
    pkg: 'fs',
    ports: {
      exec: { in: [{ id: 'in', type: 'any' }], out: [{ id: 'next', type: 'any' }] },
      data: { in: [{ id: 'prefix', type: 'string' }, { id: 'recursive', type: 'boolean' }], out: [{ id: 'entries', type: 'array' }, { id: 'nextCursor', type: 'string' }, { id: 'count', type: 'number' }] },
    },
    inputSchema: {
      type: 'object',
      properties: {
        prefix: { type: 'string' },
        recursive: { type: 'boolean', default: true },
        limit: { type: 'integer', exclusiveMinimum: 0 },
        cursor: { type: 'string' },
      },
      required: ['prefix'],
      additionalProperties: false,
    },
    outputSchema: {
      type: 'object',
      properties: {
        entries: { type: 'array' },
        nextCursor: { type: 'string' },
        count: { type: 'integer' },
      },
      required: ['entries', 'count'],
      additionalProperties: false,
    },
    attrsSchema: {
      type: 'object',
      properties: {
        recursive: { type: 'boolean', default: true },
        limit: { type: 'integer', exclusiveMinimum: 0 },
        cursor: { type: 'string' },
      },
      required: [],
      additionalProperties: false,
    },
  } as any;
  const templatesByNodeId = React.useMemo(() => ({ [nodeId]: tpl }), [nodeId]);

  return (
    <div className="relative h-[820px] border rounded grid place-items-center p-3">
      <div className="mb-2 text-xs text-muted-foreground">
        Inspector opens pinned to <span className="font-mono">{nodeId}</span>. Use tabs to view Inputs, Outputs, or Logs.
      </div>
      <NodeInspectorPanel mount="container" className="w-[1040px] h-[760px] max-h-none" pinsByNodeId={pinsByNodeId} templatesByNodeId={templatesByNodeId} />
    </div>
  );
}

const sampleInputs = { count: 3, payload: { user: { id: '42', name: 'Ada' } } };
const sampleOutputs = { result: { sum: 6, ok: true }, meta: { tookMs: 12 } };
const sampleLogs: LogEntry[] = [
  { level: 'info', msg: 'Starting transform' },
  { level: 'debug', msg: 'Loaded 3 items' },
  { level: 'warn', msg: 'Using fallback branch' },
  { level: 'info', msg: 'Done' },
];

const meta: StoryMeta<Props> = {
  id: 'flows/NodeInspectorPanel',
  title: 'NodeInspectorPanel',
  group: 'Flows/Inspectors',
  component: NodeInspectorPreview as any,
  description: 'Pinned-node inspector showing latest inputs, outputs, and runtime logs.',
  defaultProps: { nodeId: 'n1', outputs: sampleOutputs },
  controls: {
    nodeId: { type: 'text', label: 'Node ID' },
    inputs: { type: 'json', label: 'Inputs (JSON)' },
    outputs: { type: 'json', label: 'Outputs (JSON)' },
    logs: { type: 'json', label: 'Logs (array of strings or {level,msg})' },
  },
  variants: [
    { name: 'Outputs Only', props: { nodeId: 'sum@1', outputs: sampleOutputs, iterCount: 1 } },
    { name: 'Inputs + Outputs', props: { nodeId: 'sum@1', inputs: sampleInputs, outputs: sampleOutputs, iterCount: 1 } },
    { name: 'Logs', props: { nodeId: 'sum@1', logs: sampleLogs, iterCount: 1 } },
    { name: 'Iterations (3x)', props: { nodeId: 'sum@1', inputs: sampleInputs, outputs: sampleOutputs, iterCount: 3 } },
    { name: 'Iterations (5x)', props: { nodeId: 'sum@1', inputs: sampleInputs, outputs: sampleOutputs, iterCount: 5 } },
  ],
};

export default meta;
